/**
 * События года: выбор из пула и применение.
 *
 * `drawEvent`: с вероятностью EVENT_CHANCE (0.6) событие есть; затем
 * взвешенный выбор среди событий с весом > 0; при пустом пуле — null.
 * `applyEvent`: применяет эффект и возвращает записи журнала. Для тяжёлого
 * события при `flags.skipHeavy` запись получает ключ `event.neutral`, но
 * эффект применяется тот же — статистика не меняется. После тяжёлого события
 * всегда добавляется запись `help.lines` («куда обратиться», DESIGN §6) —
 * так же, как это делает `checkDeath`. Мёртвая жизнь возвращается как есть
 * без записей.
 *
 * Розыгрыш «есть ли событие» и выбор из пула идут через один `rng`
 * последовательно; `apply` получает тот же генератор.
 */
import type { Rng } from '../rng';
import type { Life, LogEntry } from '../types';
import { HELP_KEY } from '../death';
import { EVENT_DEFS, type EventCtx, type EventDef } from './defs';

export { EVENT_DEFS, FLAG_ACCIDENT_YEAR, FLAG_DOMESTIC_VIOLENCE, FLAG_LAYOFF_YEAR, FLAG_LESSON_ORDER, FLAG_LICENSE, FLAG_LOVE_FIRST, FLAG_PARENT_ILL, FLAG_PARENTS_DEAD, FLAG_TRAVELED, LAYOFF_BASE, LESSON_MATH_AWARD_MULT, LESSON_SPORT_BULLYING_MULT, LICENSE_ACCIDENT_MULT } from './defs';
export type { EventCtx, EventDef } from './defs';

/** Вероятность того, что в году вообще есть событие. */
export const EVENT_CHANCE = 0.6;
/** Нейтральный ключ для тяжёлых событий при flags.skipHeavy. */
export const EVENT_NEUTRAL_KEY = 'event.neutral';

const DEF_BY_ID: ReadonlyMap<string, EventDef> = new Map(EVENT_DEFS.map((d) => [d.id, d]));

export function eventDef(id: string): EventDef {
  const def = DEF_BY_ID.get(id);
  if (!def) throw new RangeError(`eventDef: unknown event ${id}`);
  return def;
}

/** Веса всех событий пула в порядке EVENT_DEFS; NaN и отрицательные → 0. */
export function eventWeights(ctx: EventCtx): { defs: readonly EventDef[]; weights: number[] } {
  const weights = EVENT_DEFS.map((d) => {
    const w = d.weight(ctx);
    return Number.isFinite(w) && w > 0 ? w : 0;
  });
  return { defs: EVENT_DEFS, weights };
}

/** Событие года или null (нет события в этом году либо пустой пул). */
export function drawEvent(ctx: EventCtx, rng: Rng): EventDef | null {
  if (!ctx.life.alive) return null;
  if (!rng.chance(EVENT_CHANCE)) return null;
  const { weights } = eventWeights(ctx);
  const pool: EventDef[] = [];
  const poolWeights: number[] = [];
  for (let i = 0; i < EVENT_DEFS.length; i += 1) {
    const w = weights[i]!;
    if (w > 0) {
      pool.push(EVENT_DEFS[i]!);
      poolWeights.push(w);
    }
  }
  if (pool.length === 0) return null;
  return rng.weighted(pool, poolWeights);
}

/**
 * Розыгрыш и применение события года. `entries` пуст, если события нет;
 * для тяжёлого события — две записи: само событие и `help.lines`.
 */
export function applyEvent(ctx: EventCtx, rng: Rng): { life: Life; entries: LogEntry[] } {
  const { life } = ctx;
  const def = drawEvent(ctx, rng);
  if (!def) return { life, entries: [] };
  const next = def.apply(life, rng);
  const heavy = Boolean(def.heavy);
  const skip = heavy && Boolean(life.flags['skipHeavy']);
  const entries: LogEntry[] = [{ age: life.age, key: skip ? EVENT_NEUTRAL_KEY : def.textKey, heavy }];
  if (heavy) entries.push({ age: life.age, key: HELP_KEY, heavy: true });
  return { life: next, entries };
}
